import { useMemo, useState } from "react";
import { buildClinicPortalUrl } from "../../../utils/portalHost";
import clinicAPI from "../../../services/clinicApi";
import { isPortalLoginEmail } from "./patientConstants";
import s from "../clinic.module.css";
import { Button, Field, Input, useToast } from "../../ui";

export default function ClientPortalAccessSection({
  patientId,
  patient,
  edit,
  setEdit,
  subdomain,
  newPassword,
  setNewPassword,
  onSaveEmail,
  onSetPassword,
  savingEmail = false,
  settingPassword = false,
  inFold = false,
}) {
  const toast = useToast();
  const [inviteUrl, setInviteUrl] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  const hasLogin = isPortalLoginEmail(edit.email);
  const isActivated = Boolean(patient?.is_activated);
  const loginUrl = useMemo(
    () => (subdomain ? buildClinicPortalUrl(subdomain, "/login") : ""),
    [subdomain],
  );

  const copyText = async (text, okMsg) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(okMsg || "Скопировано");
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  const sendInvite = async () => {
    if (!patientId) return;
    setSendingInvite(true);
    try {
      const r = await clinicAPI.sendClientPortalInvite(patientId);
      if (r.invite_url) setInviteUrl(r.invite_url);
      if (r.email_sent) {
        toast.success("Приглашение отправлено на email");
      } else if (r.invite_url) {
        await copyText(r.invite_url, "Ссылка приглашения скопирована");
      } else {
        toast.success(r.message || "Готово");
      }
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось отправить приглашение");
    } finally {
      setSendingInvite(false);
    }
  };

  return (
    <div className={inFold ? s.clientPortalAccessInFold : s.clientProfileEmbeddedBlock}>
      {!inFold ? (
        <div className={s.clientProfileEmbeddedHead}>
          <h3 className={s.clientTabCardTitle}>Доступ в личный кабинет</h3>
          <span className={s.clientPortalAccessStatus}>
            {!hasLogin
              ? "Нужен email"
              : isActivated
                ? "Активирован"
                : "Ожидает активации"}
          </span>
        </div>
      ) : null}

      {!hasLogin ? (
        <div className={`${s.patientAlertBanner} ${s.clientPortalAccessBanner}`}>
          Укажите реальный email родителя или клиента — сейчас служебный адрес,
          вход недоступен
        </div>
      ) : !isActivated ? (
        <div className={`${s.patientAlertBanner} ${s.clientPortalAccessBanner}`}>
          Аккаунт создан, но пароль ещё не задан. Отправьте персональную ссылку —
          она действует 72 часа.
        </div>
      ) : null}

      {hasLogin ? (
        <div className={s.clientPortalInviteRow}>
          <Button
            type="button"
            onClick={sendInvite}
            disabled={sendingInvite || !patientId}
          >
            {sendingInvite
              ? "Отправка…"
              : isActivated
                ? "Отправить ссылку снова"
                : "Отправить приглашение"}
          </Button>
          {inviteUrl ? (
            <Field label="Ссылка приглашения">
              <div className={s.inlineFieldRow}>
                <Input value={inviteUrl} readOnly />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => copyText(inviteUrl, "Ссылка скопирована")}
                >
                  Копировать
                </Button>
              </div>
            </Field>
          ) : null}
        </div>
      ) : null}

      {loginUrl && isActivated ? (
        <Field label="Ссылка для входа">
          <div className={s.inlineFieldRow}>
            <Input value={loginUrl} readOnly />
            <Button
              type="button"
              variant="secondary"
              onClick={() => copyText(loginUrl, "Ссылка скопирована")}
            >
              Копировать
            </Button>
          </div>
        </Field>
      ) : null}

      <form
        className={s.clientTabFormStack}
        onSubmit={(e) => {
          e.preventDefault();
          onSaveEmail?.(e);
        }}
      >
        <Field label="Email для входа" hint="Используется на странице входа">
          <Input
            type="email"
            value={edit.email}
            onChange={(e) => setEdit({ ...edit, email: e.target.value })}
            placeholder="client@example.com"
            autoComplete="off"
          />
        </Field>
        <Button type="submit" size="sm" disabled={savingEmail || !patientId}>
          {savingEmail ? "Сохранение…" : "Сохранить email"}
        </Button>
      </form>

      <Field label="Пароль вручную" hint="Минимум 8 символов — если нужно задать без ссылки">
        <div className={s.inlineFieldRow}>
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={onSetPassword}
            disabled={settingPassword || !patientId}
          >
            {settingPassword ? "Сохранение…" : "Задать пароль"}
          </Button>
        </div>
      </Field>
    </div>
  );
}
